import type { PolicyStatement } from ':helpers/casl/abac/policy.types';

import { isLegalActionForSubject } from ':helpers/casl/abac/legal-actions';
import { getStatementScope } from ':helpers/casl/abac/policy-scope';

export interface StatementValidationError {
    index: number;
    reason: string;
}

/**
 * Lenient write-time validation of a role's statements.
 * Every action must be legal for its subject.
 * Each statement must bind exactly one scope (organization vs restaurant) via a placeholder; getStatementScope returns null both when no scope is bound and when organization and restaurant scopes are mixed, and either case is rejected.
 * It does not force a statement to match a named level, so it stays forward-compatible with finer-grained UIs.
 */
export function validatePolicyStatements(statements: PolicyStatement[]): StatementValidationError[] {
    const errors: StatementValidationError[] = [];

    statements.forEach((statement, index) => {
        for (const action of statement.action) {
            if (!isLegalActionForSubject(action, statement.subject)) {
                errors.push({ index, reason: `Action "${action}" is not legal for subject "${statement.subject}".` });
            }
        }

        if (getStatementScope(statement.conditions) === null) {
            errors.push({
                index,
                reason: `Statement for subject "${statement.subject}" must bind a scope via $ORG_SCOPE, $RESTAURANT_SCOPE or $ORG_FROM_RESTAURANT_SCOPE in its conditions.`,
            });
        }
    });

    return errors;
}

export function arePolicyStatementsValid(statements: PolicyStatement[]): boolean {
    return validatePolicyStatements(statements).length === 0;
}
